import { useNode } from "@craftjs/core";
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, Typography } from "@mui/material";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, SelectField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

interface FloatingCTAProps {
  lineUrl?: string;
  whatsappUrl?: string;
  phoneNumber?: string;
  facebookUrl?: string;
  position?: "bottom-right" | "bottom-left";
  buttonSize?: number;
  gap?: number;
  showLabels?: boolean;
}

interface FloatingCTAButton {
  id: string;
  url: string;
  icon: string;
  label: string;
  color: string;
}

// Floating CTA Overlay - uses portal to render in preview container
interface FloatingCTAOverlayProps {
  buttons: FloatingCTAButton[];
  position: "bottom-right" | "bottom-left";
  buttonSize: number;
  gap: number;
  showLabels: boolean;
}

const FloatingCTAOverlay: React.FC<FloatingCTAOverlayProps> = ({ buttons, position, buttonSize, gap, showLabels }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Find the preview container or fall back to body
    const previewContainer = document.querySelector(".preview-container") as HTMLElement;
    setContainer(previewContainer || document.body);
    // Trigger animation after mount
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  if (!container || buttons.length === 0) return null;

  const overlay = (
    <Box
      sx={{
        position: "absolute",
        bottom: 24,
        [position === "bottom-right" ? "right" : "left"]: 24,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: `${gap}px`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
        pointerEvents: "auto",
      }}
    >
      {buttons.map((btn, index) => (
        <Box
          key={btn.id}
          component="a"
          href={btn.url}
          target={btn.id !== "phone" ? "_blank" : undefined}
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            textDecoration: "none",
            transition: "transform 0.2s",
            flexDirection: position === "bottom-right" ? "row-reverse" : "row",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : position === "bottom-right" ? "translateX(20px)" : "translateX(-20px)",
            transitionDelay: `${index * 50}ms`,
            "&:hover": { transform: "scale(1.05)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              width: `${buttonSize}px`,
              height: `${buttonSize}px`,
              backgroundColor: btn.color,
              fontSize: `${buttonSize * 0.4}px`,
            }}
          >
            {btn.icon}
          </Box>
          {showLabels && (
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "0.875rem",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: btn.color,
                whiteSpace: "nowrap",
              }}
            >
              {btn.label}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );

  return ReactDOM.createPortal(overlay, container);
};

export const FloatingCTA = ({
  lineUrl = "",
  whatsappUrl = "",
  phoneNumber = "",
  facebookUrl = "",
  position = "bottom-right",
  buttonSize = 56,
  gap = 12,
  showLabels = false,
}: FloatingCTAProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const buttons: FloatingCTAButton[] = [
    { id: "line", url: lineUrl, icon: "💬", label: "LINE", color: "#00B900" },
    { id: "whatsapp", url: whatsappUrl, icon: "📱", label: "WhatsApp", color: "#25D366" },
    { id: "phone", url: phoneNumber ? `tel:${phoneNumber}` : "", icon: "📞", label: "Call", color: "#3B82F6" },
    { id: "facebook", url: facebookUrl, icon: "📘", label: "Facebook", color: "#1877F2" },
  ].filter((btn) => btn.url) as FloatingCTAButton[];

  return (
    <>
      <Box
        ref={(ref: HTMLDivElement | null) => {
          if (ref) connect(drag(ref));
        }}
        sx={{
          ...(selected && { ...selectedOutlineSx, borderRadius: 2 }),
        }}
      >
        <Box sx={{ bgcolor: "#f3f4f6", borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "#4b5563", mb: 1 }}>
            Floating CTA
          </Typography>
          {buttons.length > 0 ? (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {buttons.map((btn) => (
                <Box
                  key={btn.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    backgroundColor: btn.color,
                    fontSize: 14,
                  }}
                >
                  {btn.icon}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#6b7280", fontSize: "0.75rem" }}>
              Add contact methods in settings
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", mt: 1 }}>
            Position: {position} • Showing in preview →
          </Typography>
        </Box>
      </Box>

      {/* Floating CTA Overlay - rendered via portal to preview container */}
      <FloatingCTAOverlay
        buttons={buttons}
        position={position}
        buttonSize={buttonSize}
        gap={gap}
        showLabels={showLabels}
      />
    </>
  );
};

const FloatingCTASettings = () => {
  const {
    actions: { setProp },
    lineUrl,
    whatsappUrl,
    phoneNumber,
    facebookUrl,
    position,
    buttonSize,
    gap,
    showLabels,
  } = useNode((node) => ({
    lineUrl: node.data.props.lineUrl,
    whatsappUrl: node.data.props.whatsappUrl,
    phoneNumber: node.data.props.phoneNumber,
    facebookUrl: node.data.props.facebookUrl,
    position: node.data.props.position,
    buttonSize: node.data.props.buttonSize,
    gap: node.data.props.gap,
    showLabels: node.data.props.showLabels,
  }));

  return (
    <SettingsSection>
      <TextField
        label="LINE URL"
        value={lineUrl || ""}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.lineUrl = v))}
        placeholder="https://line.me/ti/p/..."
      />

      <TextField
        label="WhatsApp URL"
        value={whatsappUrl || ""}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.whatsappUrl = v))}
        placeholder="https://wa.me/66..."
      />

      <TextField
        label="Phone Number"
        value={phoneNumber || ""}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.phoneNumber = v))}
        placeholder="0812345678"
      />

      <TextField
        label="Facebook Messenger URL"
        value={facebookUrl || ""}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.facebookUrl = v))}
        placeholder="https://m.me/..."
      />

      <SelectField
        label="Position"
        value={position || "bottom-right"}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.position = v as FloatingCTAProps["position"]))}
        options={[
          { value: "bottom-right", label: "Bottom Right" },
          { value: "bottom-left", label: "Bottom Left" },
        ]}
      />

      <SliderField
        label="Button Size"
        value={buttonSize || 56}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.buttonSize = v))}
        min={40}
        max={72}
        unit="px"
      />

      <SliderField
        label="Gap"
        value={gap || 12}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.gap = v))}
        min={8}
        max={24}
        unit="px"
      />

      <CheckboxField
        label="Show Labels"
        checked={showLabels || false}
        onChange={(v) => setProp((props: FloatingCTAProps) => (props.showLabels = v))}
      />
    </SettingsSection>
  );
};

FloatingCTA.craft = {
  displayName: "Floating CTA",
  props: {
    lineUrl: "",
    whatsappUrl: "",
    phoneNumber: "",
    facebookUrl: "",
    position: "bottom-right",
    buttonSize: 56,
    gap: 12,
    showLabels: false,
  },
  related: {
    settings: FloatingCTASettings,
  },
};
